import React, { useState, useRef, useEffect } from "react";
import {
  Search as SearchIcon,
  Mic,
  MicOff,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import { useWM } from "./store";
import { APPS, CATEGORIES } from "./apps";
import { useAuth } from "./AuthContext";
import { parseVoiceCommand, speak, stopSpeaking } from "./voice";
import BetaChip from "./BetaChip";

// Quick-access row at the top of the Start menu — popular apps that are NOT
// already in the taskbar (dashboard, chat, mail, browser, files, calendar,
// dayrecap) so there's no duplication of one-click access.
const PINNED_APP_IDS = [
  "wingman",
  "tasks",
  "notes",
  "teamchat",
  "meetings",
  "imagegen",
  "voiceagent",
];

// Distinct color per app — every tile gets a unique tint so later rows don't
// repeat purple/blue. Used for both the pinned row and the category sections.
const TILE_TINTS = {
  // System / core
  "admin-ai": "#fbbf24",
  dashboard: "#7c3aed",
  wingman: "#f43f5e",
  dayrecap: "#22d3ee",
  // Communication
  chat: "#6366f1",
  mail: "#3b82f6",
  teamchat: "#8b5cf6",
  meetings: "#0ea5e9",
  social: "#ef4444",
  // Productivity
  tasks: "#84cc16",
  notes: "#f59e0b",
  calendar: "#0284c7",
  approvals: "#10b981",
  attendance: "#65a30d",
  // Workspace
  files: "#eab308",
  word: "#2563eb",
  excel: "#16a34a",
  ppt: "#ea580c",
  pdf: "#dc2626",
  imagegen: "#a855f7",
  audio: "#c084fc",
  video: "#ec4899",
  // Tools
  browser: "#14b8a6",
  security: "#06b6d4",
  calculator: "#fde047",
  recorder: "#f97316",
  shortcuts: "#64748b",
  terminal: "#22c55e",
};

export default function HeroLanding({ onAsk }) {
  const { open } = useWM();
  const { user, appAccess } = useAuth();
  const [q, setQ] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceErr, setVoiceErr] = useState("");
  // If the user clicked the HEXA Start button while apps were open, we want
  // the apps grid to be visible the moment the hero remounts. The Start
  // button writes this flag *before* dispatching the event, so HeroLanding
  // can pick it up reliably regardless of mount timing.
  const [showAll, setShowAll] = useState(() => {
    if (typeof window !== "undefined" && window.__hexaShowApps) {
      window.__hexaShowApps = false;
      return true;
    }
    return false;
  });
  const recogRef = useRef(null);

  // Allow the always-on Taskbar to remotely toggle the apps grid via custom event.
  useEffect(() => {
    const onToggle = () => setShowAll((v) => !v);
    const onOpen = () => setShowAll(true);
    window.addEventListener("hexa:toggle-apps", onToggle);
    window.addEventListener("hexa:open-apps", onOpen);
    return () => {
      window.removeEventListener("hexa:toggle-apps", onToggle);
      window.removeEventListener("hexa:open-apps", onOpen);
    };
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const text = q.trim();
    if (!text) return;
    runCommand(text);
    setQ("");
  };

  const runCommand = (text) => {
    // First — local "open <app>" routing via voice.js intelligence.
    const cmd = parseVoiceCommand(text);
    if (cmd && cmd.action === "open" && cmd.appId) {
      const app = APPS.find((a) => a.id === cmd.appId);
      if (app) {
        speak(`Opening ${app.title}`);
        open(app);
        return;
      }
    }
    if (cmd && cmd.action === "stop") { stopSpeaking(); return; }

    // Second — search across pinned apps by title prefix.
    const lower = text.toLowerCase();
    const match = APPS.find((a) => a.title.toLowerCase().startsWith(lower) || a.id.toLowerCase() === lower);
    if (match) { open(match); return; }

    // Last — defer to HEXA Chat.
    onAsk?.(text);
  };

  const startVoice = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceErr("Voice not supported — please use Chrome, Edge, or Safari");
      setTimeout(() => setVoiceErr(""), 3500);
      return;
    }
    if (listening) {
      try { if (recogRef.current) { recogRef.current._userStop = true; recogRef.current.stop(); } } catch (_) { /* noop */ }
      setListening(false);
      return;
    }

    // NOTE: do NOT pre-acquire the microphone with getUserMedia here.
    // Opening and immediately releasing the input device microseconds before
    // SpeechRecognition.start() leaves the recognizer unable to open it, so
    // onresult never fires and Chrome reports "no-speech". This is the exact
    // anti-pattern documented in voice.js (probeMic is disabled for the same
    // reason). SpeechRecognition prompts for and opens the device itself,
    // which is what Spotlight.jsx and TopBar.jsx already rely on.

    const r = new SR();
    r.lang = "en-US";
    r.interimResults = true;
    // Continuous keeps the mic warm through short pauses which prevents the
    // aggressive Chrome "no-speech" timeout from firing after 2-3 seconds.
    r.continuous = true;
    r.maxAlternatives = 1;
    let finalText = "";
    let sawAnyResult = false;
    let retriedNoSpeech = false;

    r.onstart = () => setListening(true);
    r.onaudiostart = () => setVoiceErr("");
    r.onresult = (ev) => {
      let interim = "";
      let finals = "";
      for (let i = 0; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finals += t;
        else interim += t;
      }
      const combined = (finals || interim).trim();
      if (combined) sawAnyResult = true;
      setQ(combined);
      if (finals) finalText = finals;
    };
    r.onerror = (ev) => {
      const code = ev?.error || "unknown";
      // Silently retry once when the browser fires "no-speech" but the user
      // is clearly still trying — this is Chrome's aggressive VAD, not the
      // user failing to speak. Only retry when we haven't heard anything yet.
      if (code === "no-speech" && !sawAnyResult && !retriedNoSpeech && !r._userStop) {
        retriedNoSpeech = true;
        setVoiceErr("Listening… speak now");
        setTimeout(() => setVoiceErr(""), 1500);
        try { r.start(); return; } catch (_) { /* fallthrough */ }
      }
      const map = {
        "not-allowed": "Mic permission denied",
        "service-not-allowed": "Mic blocked by browser policy",
        "no-speech": "Didn't catch that — try speaking closer to the mic",
        "audio-capture": "No microphone found",
        "network": "Network error during voice",
        "aborted": "",
      };
      const label = map[code];
      if (label !== "") setVoiceErr(label || `Voice error: ${code}`);
      setTimeout(() => setVoiceErr(""), 3500);
      setListening(false);
    };
    r.onend = () => {
      setListening(false);
      const text = (finalText || "").trim();
      if (text) { runCommand(text); setQ(""); }
    };
    recogRef.current = r;
    try { r.start(); }
    catch (_) {
      setVoiceErr("Could not start mic — reload the page and try again");
      setTimeout(() => setVoiceErr(""), 3500);
    }
  };

  const pinned = PINNED_APP_IDS
    .map((id) => APPS.find((a) => a.id === id))
    .filter(Boolean)
    .filter((a) => !a.mergedInto)
    .filter((a) => !(a.requiresSuperAdmin && !user?.is_super_admin))
    .filter((a) => !appAccess?.hidden?.includes(a.id));
  // Category sections show ALL apps in their category — including taskbar
  // apps and the pinned row — so the user always sees the full org tree
  // (Communication / Productivity / Workspace / Tools). The pinned + taskbar
  // tiles are surfaced AS WELL AS appearing in their group.
  const extras = APPS
    .filter((a) => !a.mergedInto)
    .filter((a) => !(a.requiresSuperAdmin && !user?.is_super_admin))
    .filter((a) => !appAccess?.hidden?.includes(a.id))
    .filter((a) => a.id !== "admin-ai"); // surfaced as first pinned tile for super-admin
  // Surface Admin AI as the first pinned tile for super-admins.
  const adminApp = APPS.find((a) => a.id === "admin-ai");
  if (user?.is_super_admin && adminApp && !pinned.find((p) => p.id === "admin-ai")) {
    pinned.unshift(adminApp);
  }

  return (
    <div
      data-testid="hero-landing"
      className="absolute inset-0 z-[2] pointer-events-none"
    >
      {/* Landing — HEXA logo + search bar stacked in a flex column (no overlap) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 -translate-x-1/2 top-[10vh] pointer-events-auto flex flex-col items-center w-[min(680px,90vw)]" style={{ gap: "30px" }}>
          {/* HEXA brand block — full logo image (already contains the wordmark + tagline).
              We clip the bottom ~18% of the PNG to remove its internal whitespace so the
              search bar's 30px gap stays visually consistent at any viewport height. */}
          <div className="flex flex-col items-center relative">
            <img
              src="/hexa-logo.png"
              alt="HEXA — AI Web Operating System"
              style={{
                height: "clamp(240px, 40vh, 460px)",
                width: "auto",
                clipPath: "inset(0 0 18% 0)",
                marginBottom: "calc(clamp(240px, 40vh, 460px) * -0.18)",
              }}
            />
            <div className="absolute right-[-4px] top-[2px]">
              <BetaChip tone="outline" />
            </div>
          </div>

          {/* Search bar — directly below the HEXA brand */}
          <form onSubmit={submit} className="w-full">
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-full"
              style={{
                background: "rgba(12, 18, 36, 0.7)",
                backdropFilter: "blur(22px) saturate(140%)",
                WebkitBackdropFilter: "blur(22px) saturate(140%)",
                border: "1px solid rgba(139,92,246,0.22)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
            >
              <SearchIcon size={16} className="text-[#a78bfa]" />
              <input
                data-testid="start-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={listening ? "Listening…" : "Search for apps, settings, and documents"}
                className="flex-1 bg-transparent outline-none text-white/90 placeholder:text-white/45 text-sm"
                autoComplete="off"
              />
              <button
                type="button"
                data-testid="start-search-mic"
                onClick={startVoice}
                aria-label={listening ? "Stop voice" : "Voice search"}
                className="grid place-items-center w-9 h-9 rounded-full transition hover:scale-110"
                style={{
                  background: listening
                    ? "rgba(244,63,94,0.25)"
                    : "linear-gradient(135deg, rgba(139,92,246,0.32), rgba(59,130,246,0.22))",
                  color: listening ? "#fda4af" : "#c7c1ff",
                  border: `1px solid ${listening ? "rgba(244,63,94,0.5)" : "rgba(139,92,246,0.5)"}`,
                }}
              >
                {listening ? <MicOff size={14} /> : <Mic size={14} />}
              </button>
            </div>
            {voiceErr && (
              <div className="mt-2 mono text-[10px] text-[#fda4af] uppercase tracking-widest text-center">{voiceErr}</div>
            )}
          </form>
        </div>

        {/* Apps grid — toggled by the HEXA hex button in the taskbar */}
        {showAll && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[14vh] pointer-events-auto w-[min(760px,92vw)]">
            <div
              className="rounded-2xl p-4"
              style={{
                background: "rgba(12, 18, 36, 0.78)",
                backdropFilter: "blur(22px) saturate(140%)",
                WebkitBackdropFilter: "blur(22px) saturate(140%)",
                border: "1px solid rgba(139,92,246,0.22)",
                boxShadow: "0 40px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
                maxHeight: "48vh",
                overflowY: "auto",
              }}
            >
              {/* Pinned tiles — quick-access (excludes apps already on the taskbar) */}
              <div className="flex items-center gap-2 px-1 mb-1.5">
                <span className="text-base leading-none">⭐</span>
                <span
                  className="text-[10px] mono uppercase tracking-[0.22em] text-white/55"
                  style={{ fontFeatureSettings: '"tnum"' }}
                >
                  Quick Access
                </span>
                <span className="flex-1 h-px bg-white/8" />
                <span className="text-[9px] mono text-white/30">{pinned.length}</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                {pinned.map((app) => {
                  const Icon = app.icon;
                  const tint = TILE_TINTS[app.id] || "#8b5cf6";
                  return (
                    <button
                      key={app.id}
                      data-testid={`start-pin-${app.id}`}
                      onClick={() => { open(app); setShowAll(false); }}
                      title={app.title}
                      className="group flex flex-col items-center gap-1.5 px-1 pt-3 pb-2 rounded-lg hover:bg-white/8 transition"
                    >
                      <div
                        className="grid place-items-center rounded-xl"
                        style={{
                          width: 46,
                          height: 46,
                          background: `linear-gradient(160deg, ${tint}, ${tint}aa)`,
                          boxShadow: `0 6px 16px ${tint}33, inset 0 1px 0 rgba(255,255,255,0.18)`,
                        }}
                      >
                        <Icon size={22} strokeWidth={1.8} color="#fff" />
                      </div>
                      <div className="text-[10.5px] text-white/85 leading-tight text-center truncate w-full px-0.5">
                        {app.title}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Grouped categories — Workspace / Communication / Productivity / Tools */}
              {CATEGORIES.map((cat) => {
                const catApps = extras.filter((a) => a.category === cat.id);
                if (catApps.length === 0) return null;
                return (
                  <div key={cat.id} className="mt-3" data-testid={`start-category-${cat.id}`}>
                    <div className="flex items-center gap-2 px-1 mb-1.5">
                      <span className="text-base leading-none">{cat.emoji}</span>
                      <span
                        className="text-[10px] mono uppercase tracking-[0.22em] text-white/55"
                        style={{ fontFeatureSettings: '"tnum"' }}
                      >
                        {cat.label}
                      </span>
                      <span className="flex-1 h-px bg-white/8" />
                      <span className="text-[9px] mono text-white/30">{catApps.length}</span>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                      {catApps.map((app) => {
                        const Icon = app.icon;
                        const tint = TILE_TINTS[app.id] || "#8b5cf6";
                        return (
                          <button
                            key={app.id}
                            data-testid={`start-pin-${app.id}`}
                            onClick={() => { open(app); setShowAll(false); }}
                            title={app.title}
                            className="group flex flex-col items-center gap-1.5 px-1 pt-3 pb-2 rounded-lg hover:bg-white/8 transition"
                          >
                            <div
                              className="grid place-items-center rounded-xl"
                              style={{
                                width: 42,
                                height: 42,
                                background: `linear-gradient(160deg, ${tint}, ${tint}aa)`,
                                boxShadow: `0 4px 12px ${tint}26, inset 0 1px 0 rgba(255,255,255,0.16)`,
                              }}
                            >
                              <Icon size={20} strokeWidth={1.8} color="#fff" />
                            </div>
                            <div className="text-[10.5px] text-white/85 leading-tight text-center truncate w-full px-0.5">
                              {app.title}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* (Taskbar moved to its own always-rendered component in Desktop.jsx) */}
    </div>
  );
}
